/**
 * The numbers a home page's Key numbers tile can show, per shell. Pure: no React.
 *
 * The tile's saved order is `LayoutWidget.config.kpis` (ids from here). Values
 * still come from each page's own data hooks, looked up by `id`; this file only
 * names what exists, where its copy lives and which plan limit it needs.
 * The server (`src/types/dashboardLayout.ts`) mirrors `KPI_MAX` and bounds the
 * list but does not know these ids: an id the catalog lacks is dropped on read.
 */
import type { DashboardShell } from './widgetLayout';

/** Most numbers the tile can hold; mirrors `DASHBOARD_KPI_MAX` on the server. */
export const KPI_MAX = 12;

export interface KpiDefinition {
  id: string;
  /** Under `dashboardLayout.kpis.<shell>.<id>` in the locale files. */
  labelKey: string;
  noteKey: string;
  /** `PlanLimits` key the plan must grant; without it the number is neither listed nor drawn. */
  requiredLimit?: string;
}

const def = (shell: DashboardShell, id: string, requiredLimit?: string): KpiDefinition => ({
  id,
  labelKey: `dashboardLayout.kpis.${shell}.${id}.label`,
  noteKey: `dashboardLayout.kpis.${shell}.${id}.note`,
  ...(requiredLimit ? { requiredLimit } : {}),
});

/**
 * Today's store numbers, in today's order. units_30d, below_map, rebate_claims and
 * last_sync are designed but need product sign-off and a data source first.
 */
export const STORE_KPI_CATALOG: readonly KpiDefinition[] = [
  def('store', 'in_sync'),
  def('store', 'blocked'),
  def('store', 'price_mismatch'),
  def('store', 'rebates', 'rebateTracking'),
  def('store', 'missing_images'),
  def('store', 'revenue_30d'),
  def('store', 'integrations'),
  def('store', 'team'),
];

/** The vendor home's numbers, in today's order. `warehouse_products` is built for ProMaster only. */
export const VENDOR_KPI_CATALOG: readonly KpiDefinition[] = [
  def('vendor', 'products'),
  def('vendor', 'warehouse_products'),
  def('vendor', 'synced_to_stores'),
  def('vendor', 'excluded_by_stores'),
  def('vendor', 'stores_syncing'),
  def('vendor', 'feed_success_rate'),
];

export const KPI_CATALOG: Record<DashboardShell, readonly KpiDefinition[]> = {
  store: STORE_KPI_CATALOG,
  vendor: VENDOR_KPI_CATALOG,
};

export const DEFAULT_STORE_KPIS: readonly string[] = [
  'in_sync', 'blocked', 'price_mismatch', 'rebates', 'missing_images', 'revenue_30d', 'integrations', 'team',
];

export const DEFAULT_VENDOR_KPIS: readonly string[] = [
  'products', 'warehouse_products', 'synced_to_stores', 'excluded_by_stores', 'stores_syncing', 'feed_success_rate',
];

export const DEFAULT_KPIS: Record<DashboardShell, readonly string[]> = {
  store: DEFAULT_STORE_KPIS,
  vendor: DEFAULT_VENDOR_KPIS,
};
